<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';

const axios = window.axios;

const route = useRoute();
const { t, te } = useI18n();
const accountId = computed(() => route.params.accountId);

const enabled = ref(false);
const fixedPublicKey = ref('');
const publicKeyTemplate = ref(
  '{{conversation.display_id}}-{{contact.phone_number}}'
);
const defaultExpirationMinutes = ref('');
const allowAttachments = ref(true);
const hideTechnicalMetadata = ref(true);
const conversationDisplayId = ref('');
const generatedLink = ref('');
const loading = ref(false);
const generating = ref(false);

// External integration: contact lookup configuration
const contactLookupParam = ref('');
const contactLookupAttribute = ref('');
const publicApiKey = ref('');

const settingsPath = computed(
  () => `/api/v1/accounts/${accountId.value}/public_conversation_link/settings`
);

const linksPath = computed(
  () => `/api/v1/accounts/${accountId.value}/public_conversation_link/links`
);

const contactLookupUrl = computed(() => {
  if (!publicApiKey.value || !contactLookupParam.value) return '';
  const base = `${window.location.origin}/plugin/public_conversation_link/contacts/${accountId.value}/conversations`;
  return `${base}?api_key=${publicApiKey.value}&${contactLookupParam.value}=<VALOR>`;
});

const tt = (key, fallback) => (te(key) ? t(key) : fallback);

const extractErrorMessage = error => {
  const responseData = error?.response?.data;

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.error) {
    return responseData.error;
  }

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.join(', ');
  }

  if (error?.message) {
    return error.message;
  }

  return null;
};

const syncEnabledState = value => {
  window.chatwoot = window.chatwoot || {};
  window.chatwoot.plugins = window.chatwoot.plugins || {};
  const publicConversationPlugin = window.chatwoot.plugins
    .publicConversationLink || { enabledAccounts: {} };
  window.chatwoot.plugins.publicConversationLink = publicConversationPlugin;
  window.chatwoot.plugins.publicConversationLink.enabledAccounts =
    window.chatwoot.plugins.publicConversationLink.enabledAccounts || {};

  window.chatwoot.plugins.publicConversationLink.enabledAccounts[
    String(accountId.value)
  ] = value;

  window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
};

const loadSettings = async () => {
  try {
    loading.value = true;
    const { data } = await axios.get(settingsPath.value);

    enabled.value = data.enabled === true;
    fixedPublicKey.value = data.fixed_public_key || '';
    publicKeyTemplate.value =
      data.public_key_template || publicKeyTemplate.value;
    defaultExpirationMinutes.value = data.default_expiration_minutes || '';
    allowAttachments.value = data.allow_attachments !== false;
    hideTechnicalMetadata.value = data.hide_technical_metadata !== false;
    contactLookupParam.value = data.contact_lookup_param || '';
    contactLookupAttribute.value = data.contact_lookup_attribute || '';
    publicApiKey.value = data.public_api_key || '';

    syncEnabledState(enabled.value);
  } catch (error) {
    useAlert(extractErrorMessage(error) || 'Unable to load public conversation settings.');
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  try {
    loading.value = true;
    await persistSettings();
    useAlert('Public conversation settings saved.');
  } catch (error) {
    useAlert(extractErrorMessage(error) || 'Unable to save public conversation settings.');
  } finally {
    loading.value = false;
  }
};

const persistSettings = async () => {
  await axios.put(settingsPath.value, {
    enabled: enabled.value,
    fixed_public_key: fixedPublicKey.value,
    public_key_template: publicKeyTemplate.value,
    default_expiration_minutes: defaultExpirationMinutes.value || null,
    allow_attachments: allowAttachments.value,
    hide_technical_metadata: hideTechnicalMetadata.value,
    contact_lookup_param: contactLookupParam.value || null,
    contact_lookup_attribute: contactLookupAttribute.value || null,
  });

  syncEnabledState(enabled.value);
};

const generateLink = async () => {
  if (!enabled.value) {
    useAlert('Enable and save account settings before generating a public link.');
    return;
  }

  try {
    generating.value = true;
    await persistSettings();

    const { data } = await axios.post(linksPath.value, {
      conversation_display_id: conversationDisplayId.value || undefined,
      expires_in_minutes: defaultExpirationMinutes.value || undefined,
    });

    generatedLink.value = `${window.location.origin}${data.link}`;
  } catch (error) {
    useAlert(extractErrorMessage(error) || 'Unable to generate public link.');
  } finally {
    generating.value = false;
  }
};

onMounted(loadSettings);
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-10">
    <template #header>
      <BaseSettingsHeader
        :title="tt('PUBLIC_CONVERSATION_LINK.TITLE', 'Public conversations')"
        :description="tt('PUBLIC_CONVERSATION_LINK.SETTINGS.DESCRIPTION', 'Configure account-level defaults for public conversation links. Inbox settings can override these defaults.')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 max-w-3xl">
        <div class="flex items-center gap-3">
          <Switch v-model="enabled" />
          <span class="text-sm text-n-slate-12">
            {{ tt('PUBLIC_CONVERSATION_LINK.SETTINGS.ENABLE_ACCOUNT', 'Enable public conversation links for this account') }}
          </span>
        </div>

        <Input
          v-model="fixedPublicKey"
          label="Fixed public key"
          placeholder="Optional fixed key"
        />

        <Input
          v-model="publicKeyTemplate"
          label="Public key template"
          placeholder="{{conversation.display_id}}-{{contact.phone_number}}"
        />

        <Input
          v-model="defaultExpirationMinutes"
          label="Default expiration (minutes)"
          type="number"
          placeholder="Leave empty for no expiration"
        />

        <div class="flex items-center gap-3">
          <Switch v-model="allowAttachments" />
          <span class="text-sm text-n-slate-12">
            {{ tt('PUBLIC_CONVERSATION_LINK.SETTINGS.ALLOW_ATTACHMENTS', 'Allow attachments in public view') }}
          </span>
        </div>

        <div class="flex items-center gap-3">
          <Switch v-model="hideTechnicalMetadata" />
          <span class="text-sm text-n-slate-12">
            {{ tt('PUBLIC_CONVERSATION_LINK.SETTINGS.HIDE_METADATA', 'Hide technical metadata') }}
          </span>
        </div>

        <div class="flex gap-2">
          <NextButton :is-loading="loading" @click="saveSettings">
            {{ tt('PUBLIC_CONVERSATION_LINK.SETTINGS.SAVE', 'Save settings') }}
          </NextButton>
        </div>

        <hr class="border-n-slate-4" />

        <!-- ── Integração Externa ─────────────────────────────────── -->
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Integração externa por contato</h3>
          <p class="text-xs text-n-slate-11 mb-4">
            Permite que seu site consulte conversas públicas de um contato sem autenticação,
            passando apenas a chave de API e o valor do atributo configurado.
          </p>
          <div class="flex flex-col gap-4">
            <Input
              v-model="contactLookupParam"
              label="Nome do parâmetro na URL"
              placeholder="ex: client_id"
              :help-text="'Nome do query param que seu site vai passar. Ex: ?client_id=123'"
            />
            <Input
              v-model="contactLookupAttribute"
              label="Atributo do contato"
              placeholder="ex: email, phone_number, external_id"
              :help-text="'Atributo do contato que será comparado. Pode ser email, phone_number, identifier ou qualquer chave de additional_attributes.'"
            />
            <div v-if="publicApiKey" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">Chave de API pública (auto-gerada)</label>
              <div class="flex items-center gap-2">
                <input
                  :value="publicApiKey"
                  readonly
                  class="flex-1 text-xs font-mono bg-n-slate-2 border border-n-slate-4 rounded px-3 py-2 text-n-slate-12 select-all"
                />
              </div>
              <p class="text-xs text-n-slate-11">Inclua essa chave como <code class="font-mono bg-n-slate-2 px-1 rounded">api_key</code> nas requisições do seu site.</p>
            </div>
            <div v-if="contactLookupUrl" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">URL de exemplo</label>
              <input
                :value="contactLookupUrl"
                readonly
                class="w-full text-xs font-mono bg-n-slate-2 border border-n-slate-4 rounded px-3 py-2 text-n-slate-12 select-all"
              />
              <p class="text-xs text-n-slate-11">
                Adicione <code class="font-mono bg-n-slate-2 px-1 rounded">&amp;conversation_id=42</code> para filtrar uma conversa específica.
              </p>
            </div>
          </div>
        </div>

        <hr class="border-n-slate-4" />

        <Input
          v-model="conversationDisplayId"
          label="Conversation display ID"
          placeholder="Optional - leave empty to use latest conversation"
        />

        <div class="flex gap-2">
          <NextButton
            :is-loading="generating"
            :disabled="!enabled"
            @click="generateLink"
          >
            {{ tt('PUBLIC_CONVERSATION_LINK.SETTINGS.GENERATE', 'Generate public link') }}
          </NextButton>
        </div>

        <Input
          v-if="generatedLink"
          :model-value="generatedLink"
          label="Generated link"
          readonly
        />
      </div>
    </template>
  </SettingsLayout>
</template>
